import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { assetApi } from '../api/assetApi';
import { marketDataApi } from '../api/marketDataApi';
import { schwabApi } from '../api/schwabApi';
import { useQuote } from '../contexts/QuoteContext';

function assetTypeBadge(assetType) {
  const t = String(assetType || '').toUpperCase();
  if (!t) return null;
  if (t === 'MUTUAL_FUND') return { label: 'Fund', className: 'bg-amber-100 text-amber-900' };
  if (t === 'ETF') return { label: 'ETF', className: 'bg-sky-100 text-sky-900' };
  if (t === 'OPTION') return { label: 'Opt', className: 'bg-purple-100 text-purple-900' };
  if (t === 'INDEX') return { label: 'Idx', className: 'bg-slate-100 text-slate-700' };
  if (t === 'EQUITY') return { label: 'Eq', className: 'bg-[var(--lt-action-dim)] text-action' };
  return { label: t.slice(0, 6), className: 'bg-elevated text-muted' };
}

function looksLikeNameQuery(q) {
  return /\s/.test(q) || q.length > 8;
}

const AssetSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [schwabConnected, setSchwabConnected] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const { selectSymbol } = useQuote();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const statusRes = await schwabApi.getStatus();
        if (!cancelled) {
          setSchwabConnected(Boolean(statusRes?.connected || statusRes?.data?.connected));
        }
      } catch {
        if (!cancelled) setSchwabConnected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length >= 1) {
      const debounceTimer = setTimeout(() => {
        searchAssets(query);
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
    setResults([]);
    setShowResults(false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, schwabConnected]);

  const searchSchwab = async (trimmed) => {
    if (!schwabConnected) return [];
    const projection = looksLikeNameQuery(trimmed) ? 'desc-search' : 'symbol-search';
    try {
      const res = await marketDataApi.searchInstruments({
        symbol: trimmed,
        projection,
        source: 'schwab',
      });
      return (res.instruments || []).map((i) => ({
        symbol: String(i.symbol || '').toUpperCase(),
        name: i.name || i.description || i.symbol,
        exchange: i.exchange || null,
        assetType: i.assetType || null,
        cusip: i.cusip || null,
        source: 'schwab',
        tradable: true,
      }));
    } catch (err) {
      console.warn('Schwab instrument search failed:', err.response?.data?.error || err.message);
      return [];
    }
  };

  const searchAssets = async (searchQuery) => {
    try {
      setLoading(true);
      const trimmed = searchQuery.trim().toUpperCase();

      const [alpacaRes, schwabList] = await Promise.all([
        assetApi.searchAssets(trimmed, 12, true).catch(() => ({ data: [] })),
        searchSchwab(trimmed),
      ]);

      let assets = (alpacaRes.data || []).map((a) => ({
        ...a,
        source: a.source || 'alpaca',
        assetType: a.assetType || a.class || 'EQUITY',
      }));

      const hasExactAlpaca = assets.some((asset) => String(asset.symbol).toUpperCase() === trimmed);
      if (!hasExactAlpaca && /^[A-Z0-9.\-/]{1,12}$/.test(trimmed)) {
        try {
          const exact = await assetApi.getAssetBySymbol(trimmed);
          if (exact.data) {
            assets = [
              { ...exact.data, source: 'alpaca', assetType: exact.data.class || 'EQUITY' },
              ...assets.filter((asset) => asset.symbol !== exact.data.symbol),
            ];
          }
        } catch {
          // not in local catalog
        }
      }

      // Merge Schwab (includes mutual funds). Prefer Schwab assetType when both match.
      const bySymbol = new Map();
      for (const a of assets) {
        if (!a.symbol) continue;
        bySymbol.set(String(a.symbol).toUpperCase(), a);
      }
      for (const s of schwabList) {
        if (!s.symbol) continue;
        const key = s.symbol;
        const existing = bySymbol.get(key);
        if (!existing) {
          bySymbol.set(key, s);
        } else {
          bySymbol.set(key, {
            ...existing,
            ...s,
            name: s.name || existing.name,
            tradable: existing.tradable ?? s.tradable,
            source: existing.source === 'alpaca' ? 'both' : 'schwab',
          });
        }
      }

      let merged = [...bySymbol.values()];
      const exact = merged.filter((a) => String(a.symbol).toUpperCase() === trimmed);
      const funds = merged.filter(
        (a) =>
          String(a.symbol).toUpperCase() !== trimmed &&
          String(a.assetType || '').toUpperCase() === 'MUTUAL_FUND'
      );
      const rest = merged.filter(
        (a) =>
          String(a.symbol).toUpperCase() !== trimmed &&
          String(a.assetType || '').toUpperCase() !== 'MUTUAL_FUND'
      );
      merged = [...exact, ...funds, ...rest].slice(0, 20);

      setResults(merged);
      setShowResults(true);
      setSelectedIndex(merged.length > 0 ? 0 : -1);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAsset = (asset) => {
    setQuery('');
    setShowResults(false);
    selectSymbol(asset.symbol, asset);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setQuery('');
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length === 0) return;
      const exact = results.find(
        (asset) => String(asset.symbol).toUpperCase() === query.trim().toUpperCase()
      );
      handleSelectAsset(exact || results[selectedIndex >= 0 ? selectedIndex : 0]);
      return;
    }

    if (!showResults || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    }
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted">
          <FontAwesomeIcon
            icon={loading ? faSpinner : faSearch}
            className={`text-xs ${loading ? 'animate-spin' : ''}`}
          />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 1 && results.length > 0 && setShowResults(true)}
          placeholder={schwabConnected ? 'Search stocks & mutual funds' : 'Search symbol'}
          className="w-full h-9 pl-9 pr-8 lt-input text-sm num placeholder:font-sans"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setShowResults(false);
            }}
            className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-muted hover:text-ink"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div
          ref={resultsRef}
          className="absolute z-50 w-full mt-1.5 lt-card max-h-96 overflow-y-auto"
        >
          {results.map((asset, index) => {
            const badge = assetTypeBadge(asset.assetType);
            return (
              <button
                key={`${asset.source || 'x'}-${asset.symbol}-${asset.cusip || ''}`}
                onClick={() => handleSelectAsset(asset)}
                className={`w-full px-3 py-2.5 text-left min-h-[44px] border-b border-hairline last:border-b-0 transition-colors ${
                  index === selectedIndex ? 'bg-elevated' : 'hover:bg-elevated'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-ink num text-[15px]">{asset.symbol}</span>
                      {badge && (
                        <span
                          className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      )}
                      {asset.tradable && String(asset.assetType || '').toUpperCase() !== 'MUTUAL_FUND' && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm bg-[var(--lt-action-dim)] text-action">
                          Trade
                        </span>
                      )}
                    </div>
                    {asset.name && (
                      <div className="text-xs text-muted mt-0.5 truncate">{asset.name}</div>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted flex-shrink-0 text-right">
                    {asset.exchange || ''}
                    {asset.source === 'schwab' || asset.source === 'both' ? (
                      <div className="text-[9px] text-blue-700/80 normal-case tracking-normal">Schwab</div>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showResults && query.length >= 1 && !loading && results.length === 0 && (
        <div className="absolute z-50 w-full mt-1.5 lt-card p-3 text-center text-muted text-sm">
          No assets found
          {!schwabConnected ? (
            <div className="text-[11px] mt-1">Connect Schwab to search mutual funds</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AssetSearch;
